/**
 * Scene graph - main API for managing the document tree
 */

import type { NodeId, NodeType, PropertyPath } from '@core/types/common';
import type { Matrix2x3, Rect } from '@core/types/geometry';
import { identity, multiply, compose, transformPoint, invert } from '@core/math/matrix';
import { EventEmitter } from '@core/events/event-emitter';
import type { NodeData } from '../nodes/base-node';
import {
  createDocument,
  createPage,
  createFrame,
  createGroup,
  createVector,
  createImage,
  createText,
  createComponent,
  createInstance,
  createBooleanOperation,
  createSlice,
} from '../nodes/factory';
import { NodeRegistry } from './node-registry';
import {
  insertNode,
  deleteNode,
  moveNode,
  reorderNode,
  wouldCreateCycle,
  isValidParentChild,
  getNodeDepth,
  findCommonAncestor,
  duplicateNode as duplicateNodeTree,
} from './tree-operations';
import { generateNodeId } from '@core/utils/uuid';
import { generateFirstIndex } from './fractional-index';

/**
 * Scene graph events
 */
export type SceneGraphEvents = {
  'node:created': { nodeId: NodeId; nodeType: NodeType };
  'node:deleted': { nodeId: NodeId; nodeType: NodeType; parentId: NodeId | null };
  'node:propertyChanged': {
    nodeId: NodeId;
    path: PropertyPath;
    oldValue: unknown;
    newValue: unknown;
  };
  'node:parentChanged': {
    nodeId: NodeId;
    oldParentId: NodeId | null;
    newParentId: NodeId | null;
  };
  'node:childrenReordered': { parentId: NodeId };
  'document:loaded': undefined;
  'document:cleared': undefined;
  [key: string]: unknown;
};

/**
 * Options for creating nodes
 */
export type CreateNodeOptions = Partial<Omit<NodeData, 'id' | 'type'>>;

/**
 * Scene graph - the core data structure for the document
 */
export class SceneGraph extends EventEmitter<SceneGraphEvents> {
  private registry = new NodeRegistry();

  constructor() {
    super();
  }

  // =========================================================================
  // Document Lifecycle
  // =========================================================================

  /**
   * Create a new empty document with one page.
   */
  createNewDocument(name?: string): NodeId {
    this.clear();

    // Create document
    const doc = createDocument(name ? { name } : {});
    this.registry.addNode(doc, generateFirstIndex());

    // Create first page (called "Leaf" in the UI)
    const page = createPage({ name: 'Leaf 1' });
    insertNode(this.registry, page, doc.id, 0);

    this.emit('node:created', { nodeId: doc.id, nodeType: 'DOCUMENT' });
    this.emit('node:created', { nodeId: page.id, nodeType: 'PAGE' });
    this.emit('document:loaded');

    return doc.id;
  }

  /**
   * Clear all nodes.
   */
  clear(): void {
    this.registry.clear();
    this.emit('document:cleared');
  }

  // =========================================================================
  // Node Access
  // =========================================================================

  /**
   * Get a node by ID.
   */
  getNode(id: NodeId): NodeData | null {
    return this.registry.getNode(id);
  }

  /**
   * Check if a node exists.
   */
  hasNode(id: NodeId): boolean {
    return this.registry.hasNode(id);
  }

  /**
   * Get the document node.
   */
  getDocument(): NodeData | null {
    return this.registry.getDocument();
  }

  /**
   * Get all pages.
   */
  getPages(): NodeData[] {
    return this.registry.getPages();
  }

  /**
   * Get children of a node.
   */
  getChildren(parentId: NodeId): NodeData[] {
    return this.registry.getChildren(parentId);
  }

  /**
   * Get child IDs of a node.
   */
  getChildIds(parentId: NodeId): NodeId[] {
    return this.registry.getChildIds(parentId);
  }

  /**
   * Get the parent of a node.
   */
  getParent(id: NodeId): NodeData | null {
    return this.registry.getParent(id);
  }

  /**
   * Get ancestors of a node.
   */
  getAncestors(id: NodeId): NodeData[] {
    return this.registry.getAncestors(id);
  }

  /**
   * Get descendants of a node.
   */
  getDescendants(id: NodeId): NodeData[] {
    return this.registry.getDescendants(id);
  }

  /**
   * Get nodes by type.
   */
  getNodesByType(type: NodeType): NodeData[] {
    return this.registry.getNodesByType(type)
      .map((id) => this.registry.getNode(id))
      .filter((n): n is NodeData => n !== null);
  }

  /**
   * Find nodes matching a predicate.
   */
  findNodes(predicate: (node: NodeData) => boolean): NodeData[] {
    return this.registry.findNodes(predicate);
  }

  /**
   * Get node depth in tree.
   */
  getDepth(id: NodeId): number {
    return getNodeDepth(this.registry, id);
  }

  /**
   * Find common ancestor of nodes.
   */
  getCommonAncestor(ids: readonly NodeId[]): NodeId | null {
    return findCommonAncestor(this.registry, ids);
  }

  /**
   * Get the world transform for a node by walking up the parent chain.
   * This accumulates all parent transforms to get the final world position.
   */
  getWorldTransform(id: NodeId): Matrix2x3 {
    const node = this.registry.getNode(id);
    if (!node) return identity();

    // Build transform chain from root to this node
    const ancestors = this.registry.getAncestors(id);

    // Start with identity and multiply through the chain
    // Ancestors are in order from immediate parent to root,
    // so we need to reverse to go root -> parent -> node
    let worldTransform = identity();

    // Apply ancestor transforms (root to parent)
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const ancestor = ancestors[i];
      if (ancestor && 'x' in ancestor && 'y' in ancestor) {
        const a = ancestor as { x: number; y: number; rotation?: number };
        const rotation = a.rotation ? (a.rotation * Math.PI) / 180 : 0;
        const localTransform = compose(a.x, a.y, rotation, 1, 1);
        worldTransform = multiply(worldTransform, localTransform);
      }
    }

    // Apply this node's transform
    if ('x' in node && 'y' in node) {
      const n = node as { x: number; y: number; rotation?: number };
      const rotation = n.rotation ? (n.rotation * Math.PI) / 180 : 0;
      const localTransform = compose(n.x, n.y, rotation, 1, 1);
      worldTransform = multiply(worldTransform, localTransform);
    }

    return worldTransform;
  }

  /**
   * Get the world bounds for a node (accounting for parent transforms).
   * Returns the axis-aligned bounding box in world coordinates.
   */
  getWorldBounds(id: NodeId): Rect | null {
    const node = this.registry.getNode(id);
    if (!node) return null;

    if (!('x' in node) || !('y' in node) || !('width' in node) || !('height' in node)) {
      return null;
    }

    const n = node as { x: number; y: number; width: number; height: number; rotation?: number };
    const worldTransform = this.getWorldTransform(id);

    // Transform all 4 corners of the local bounding box
    // Local bounds are (0, 0) to (width, height) - the node's x, y is already in the transform
    // Actually, the node's x, y is part of the transform, so local geometry starts at (0, 0)
    const corners = [
      { x: 0, y: 0 },
      { x: n.width, y: 0 },
      { x: n.width, y: n.height },
      { x: 0, y: n.height },
    ];

    const worldCorners = corners.map(c => transformPoint(worldTransform, c));

    // Find axis-aligned bounding box
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    for (const corner of worldCorners) {
      minX = Math.min(minX, corner.x);
      minY = Math.min(minY, corner.y);
      maxX = Math.max(maxX, corner.x);
      maxY = Math.max(maxY, corner.y);
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  // =========================================================================
  // Node Creation
  // =========================================================================

  /**
   * Create a node of the specified type.
   */
  createNode(
    type: NodeType,
    parentId: NodeId,
    position: number = -1,
    options: CreateNodeOptions = {}
  ): NodeId {
    let node: NodeData;

    switch (type) {
      case 'DOCUMENT':
        throw new Error('Cannot create document node directly');
      case 'PAGE':
        node = createPage(options as Parameters<typeof createPage>[0]);
        break;
      case 'FRAME':
        node = createFrame(options as Parameters<typeof createFrame>[0]);
        break;
      case 'GROUP':
        node = createGroup(options as Parameters<typeof createGroup>[0]);
        break;
      case 'VECTOR':
        node = createVector(options as Parameters<typeof createVector>[0]);
        break;
      case 'IMAGE':
        if (!('imageRef' in options)) {
          throw new Error('imageRef required for IMAGE');
        }
        node = createImage(options as Parameters<typeof createImage>[0]);
        break;
      case 'TEXT':
        node = createText(options as Parameters<typeof createText>[0]);
        break;
      case 'COMPONENT':
        node = createComponent(options as Parameters<typeof createComponent>[0]);
        break;
      case 'INSTANCE':
        if (!('componentId' in options)) {
          throw new Error('componentId required for INSTANCE');
        }
        node = createInstance(options as Parameters<typeof createInstance>[0]);
        break;
      case 'BOOLEAN_OPERATION':
        node = createBooleanOperation(
          options as Parameters<typeof createBooleanOperation>[0]
        );
        break;
      case 'SLICE':
        node = createSlice(options as Parameters<typeof createSlice>[0]);
        break;
      default:
        throw new Error(`Unknown node type: ${type}`);
    }

    const actualPosition = position < 0
      ? this.registry.getChildIds(parentId).length
      : position;

    insertNode(this.registry, node, parentId, actualPosition);

    this.emit('node:created', { nodeId: node.id, nodeType: type });

    return node.id;
  }

  /**
   * Create a frame node.
   */
  createFrame(
    parentId: NodeId,
    options: Parameters<typeof createFrame>[0] = {}
  ): NodeId {
    return this.createNode('FRAME', parentId, -1, options);
  }

  /**
   * Create a vector node.
   */
  createVector(
    parentId: NodeId,
    options: Parameters<typeof createVector>[0] = {}
  ): NodeId {
    return this.createNode('VECTOR', parentId, -1, options);
  }

  /**
   * Create a text node.
   */
  createText(
    parentId: NodeId,
    options: Parameters<typeof createText>[0] = {}
  ): NodeId {
    return this.createNode('TEXT', parentId, -1, options);
  }

  /**
   * Create an image node.
   */
  createImage(
    parentId: NodeId,
    options: Parameters<typeof createImage>[0]
  ): NodeId {
    return this.createNode('IMAGE', parentId, -1, options);
  }

  // =========================================================================
  // Node Modification
  // =========================================================================

  /**
   * Update a node's properties.
   */
  updateNode(id: NodeId, updates: Partial<NodeData>): void {
    const node = this.registry.getNode(id);
    if (!node) {
      throw new Error(`Node ${id} not found`);
    }

    // Track changes for events
    const changes: Array<{ path: PropertyPath; oldValue: unknown; newValue: unknown }> = [];

    for (const [key, value] of Object.entries(updates)) {
      if (key !== 'id' && key !== 'type') {
        const oldValue = (node as unknown as Record<string, unknown>)[key];
        if (oldValue !== value) {
          changes.push({ path: [key], oldValue, newValue: value });
        }
      }
    }

    // Apply updates
    this.registry.updateNode(id, (n) => ({
      ...n,
      ...updates,
      id: n.id, // Preserve id
      type: n.type, // Preserve type
    }) as NodeData);

    // Emit events
    for (const change of changes) {
      this.emit('node:propertyChanged', {
        nodeId: id,
        path: change.path,
        oldValue: change.oldValue,
        newValue: change.newValue,
      });
    }
  }

  /**
   * Set a specific property on a node.
   */
  setProperty<K extends keyof NodeData>(
    id: NodeId,
    key: K,
    value: NodeData[K]
  ): void {
    this.updateNode(id, { [key]: value } as Partial<NodeData>);
  }

  // =========================================================================
  // Node Operations
  // =========================================================================

  /**
   * Delete a node and its descendants.
   */
  deleteNode(id: NodeId): void {
    const node = this.registry.getNode(id);
    if (!node) return;

    const deleted = deleteNode(this.registry, id);

    for (const n of deleted) {
      this.emit('node:deleted', {
        nodeId: n.id,
        nodeType: n.type,
        parentId: n.parentId,
      });
    }
  }

  /**
   * Move a node to a new parent, preserving its world position.
   */
  moveNode(nodeId: NodeId, newParentId: NodeId, position: number = -1): void {
    const node = this.registry.getNode(nodeId);
    if (!node) {
      throw new Error(`Node ${nodeId} not found`);
    }

    const oldParentId = node.parentId;

    // Skip coordinate adjustment if parent isn't changing
    if (oldParentId === newParentId) {
      const actualPosition = position < 0
        ? this.registry.getChildIds(newParentId).length
        : position;
      moveNode(this.registry, nodeId, newParentId, actualPosition);
      this.emit('node:parentChanged', { nodeId, oldParentId, newParentId });
      return;
    }

    // Calculate current world position of the node
    // We need the world position of the node's origin (its x, y in world space)
    const nodeWithPos = node as { x?: number; y?: number; rotation?: number };
    if (nodeWithPos.x === undefined || nodeWithPos.y === undefined) {
      // Node doesn't have position, just move it
      const actualPosition = position < 0
        ? this.registry.getChildIds(newParentId).length
        : position;
      moveNode(this.registry, nodeId, newParentId, actualPosition);
      this.emit('node:parentChanged', { nodeId, oldParentId, newParentId });
      return;
    }

    // Get parent's world transform (not including this node)
    // The parent chain transform positions this node
    let oldParentWorldTransform = identity();
    if (oldParentId) {
      const ancestors = this.registry.getAncestors(nodeId);
      // ancestors[0] is immediate parent, last is root
      for (let i = ancestors.length - 1; i >= 0; i--) {
        const ancestor = ancestors[i];
        if (ancestor && 'x' in ancestor && 'y' in ancestor) {
          const a = ancestor as { x: number; y: number; rotation?: number };
          const rotation = a.rotation ? (a.rotation * Math.PI) / 180 : 0;
          const localTransform = compose(a.x, a.y, rotation, 1, 1);
          oldParentWorldTransform = multiply(oldParentWorldTransform, localTransform);
        }
      }
    }

    // Current world position of this node's origin
    const worldPos = transformPoint(oldParentWorldTransform, { x: nodeWithPos.x, y: nodeWithPos.y });

    // Perform the actual move
    const actualPosition = position < 0
      ? this.registry.getChildIds(newParentId).length
      : position;
    moveNode(this.registry, nodeId, newParentId, actualPosition);

    // Calculate new parent's world transform
    let newParentWorldTransform = identity();
    const newParent = this.registry.getNode(newParentId);
    if (newParent) {
      // Get new parent's ancestors + new parent itself
      const newAncestors = this.registry.getAncestors(newParentId);
      // Apply ancestors (root to parent of newParent)
      for (let i = newAncestors.length - 1; i >= 0; i--) {
        const ancestor = newAncestors[i];
        if (ancestor && 'x' in ancestor && 'y' in ancestor) {
          const a = ancestor as { x: number; y: number; rotation?: number };
          const rotation = a.rotation ? (a.rotation * Math.PI) / 180 : 0;
          const localTransform = compose(a.x, a.y, rotation, 1, 1);
          newParentWorldTransform = multiply(newParentWorldTransform, localTransform);
        }
      }
      // Apply new parent's transform
      if ('x' in newParent && 'y' in newParent) {
        const np = newParent as { x: number; y: number; rotation?: number };
        const rotation = np.rotation ? (np.rotation * Math.PI) / 180 : 0;
        const localTransform = compose(np.x, np.y, rotation, 1, 1);
        newParentWorldTransform = multiply(newParentWorldTransform, localTransform);
      }
    }

    // Calculate new local position: we need localPos such that
    // transformPoint(newParentWorldTransform, localPos) = worldPos
    // Use inverse transform for proper handling of rotation/scale
    const inverseParentTransform = invert(newParentWorldTransform);

    let newLocalX: number;
    let newLocalY: number;

    if (inverseParentTransform) {
      // Apply inverse transform to get local position
      const localPos = transformPoint(inverseParentTransform, worldPos);
      newLocalX = localPos.x;
      newLocalY = localPos.y;
    } else {
      // Fallback: extract translation (matrix format is [a, b, c, d, tx, ty])
      const parentWorldX = newParentWorldTransform[4];
      const parentWorldY = newParentWorldTransform[5];
      newLocalX = worldPos.x - parentWorldX;
      newLocalY = worldPos.y - parentWorldY;
    }

    // Update the node's position
    this.updateNode(nodeId, { x: newLocalX, y: newLocalY } as Partial<NodeData>);

    this.emit('node:parentChanged', {
      nodeId,
      oldParentId,
      newParentId,
    });
  }

  /**
   * Reorder a node within its parent.
   */
  reorderNode(nodeId: NodeId, newPosition: number): void {
    const node = this.registry.getNode(nodeId);
    if (!node?.parentId) return;

    reorderNode(this.registry, nodeId, newPosition);

    this.emit('node:childrenReordered', { parentId: node.parentId });
  }

  /**
   * Duplicate a node and all its descendants.
   * Returns the new node ID, or null if duplication failed.
   */
  duplicateNode(nodeId: NodeId, offset?: { x?: number; y?: number }): NodeId | null {
    const node = this.registry.getNode(nodeId);
    if (!node) return null;

    const cloned = duplicateNodeTree(this.registry, nodeId, generateNodeId, offset);
    if (!cloned) return null;

    // Emit creation events for the duplicated tree
    const emitCreated = (n: NodeData): void => {
      this.emit('node:created', { nodeId: n.id, nodeType: n.type });
      for (const childId of n.childIds) {
        const child = this.registry.getNode(childId);
        if (child) emitCreated(child);
      }
    };

    const duplicated = this.registry.getNode(cloned.id);
    if (duplicated) {
      emitCreated(duplicated);
    }

    return cloned.id;
  }

  /**
   * Duplicate multiple nodes. Returns array of new node IDs.
   */
  duplicateNodes(nodeIds: NodeId[], offset?: { x?: number; y?: number }): NodeId[] {
    const newIds: NodeId[] = [];
    for (const nodeId of nodeIds) {
      const newId = this.duplicateNode(nodeId, offset);
      if (newId) {
        newIds.push(newId);
      }
    }
    return newIds;
  }

  /**
   * Check if a move would create a cycle.
   */
  wouldCreateCycle(nodeId: NodeId, newParentId: NodeId): boolean {
    return wouldCreateCycle(this.registry, nodeId, newParentId);
  }

  /**
   * Check if a node can be a child of another.
   */
  canBeChildOf(childType: NodeType, parentType: NodeType): boolean {
    return isValidParentChild(parentType, childType);
  }

  // =========================================================================
  // Traversal
  // =========================================================================

  /**
   * Traverse the tree depth-first.
   */
  traverse(
    visitor: (node: NodeData, depth: number) => boolean | void,
    startId?: NodeId
  ): void {
    const start = startId
      ? this.registry.getNode(startId)
      : this.registry.getDocument();

    if (!start) return;

    const stack: Array<{ node: NodeData; depth: number }> = [
      { node: start, depth: 0 },
    ];

    while (stack.length > 0) {
      const { node, depth } = stack.pop()!;

      const result = visitor(node, depth);
      if (result === false) continue;

      // Add children in reverse order so they're processed in order
      const children = this.registry.getChildren(node.id);
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i]!, depth: depth + 1 });
      }
    }
  }

  // =========================================================================
  // Versioning
  // =========================================================================

  /**
   * Get the global version of the scene graph.
   */
  getVersion(): number {
    return this.registry.getGlobalVersion();
  }

  /**
   * Get the version of a specific node.
   */
  getNodeVersion(id: NodeId): number {
    return this.registry.getNodeVersion(id);
  }

  // =========================================================================
  // Statistics
  // =========================================================================

  /**
   * Get the total number of nodes.
   */
  get nodeCount(): number {
    return this.registry.size;
  }

  /**
   * Get all node IDs.
   */
  getAllNodeIds(): NodeId[] {
    return this.registry.getAllIds();
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /**
   * Export the scene graph as JSON.
   */
  toJSON(): Record<string, unknown> {
    return {
      version: '1.0.0',
      nodes: this.registry.toJSON(),
    };
  }

  /**
   * Serialize all nodes to a portable format.
   * Used for state snapshotting and undo/redo.
   */
  serializeAll(): { version: string; nodes: NodeData[]; parentMap: Record<string, string | null> } {
    const nodes: NodeData[] = [];
    const parentMap: Record<string, string | null> = {};

    this.traverse((node) => {
      nodes.push({ ...node } as NodeData);
      parentMap[node.id] = node.parentId ?? null;
    });

    return {
      version: '1.0.0',
      nodes,
      parentMap,
    };
  }

  /**
   * Restore the scene graph from serialized data.
   * Used for state restoration and undo/redo.
   */
  deserializeAll(data: { version: string; nodes: NodeData[]; parentMap: Record<string, string | null> }): void {
    this.clear();

    // First pass: add all nodes
    for (const node of data.nodes) {
      this.registry.addNode(node, generateFirstIndex());
    }

    // Second pass: restore parent relationships
    for (const node of data.nodes) {
      const parentId = data.parentMap[node.id];
      if (parentId) {
        this.registry.updateNode(node.id, (n) => ({
          ...n,
          parentId: parentId as NodeId,
        }) as NodeData);
      }
    }

    this.emit('document:loaded');
  }

  /**
   * Get a node by ID (alias for getNode for API compatibility).
   */
  getNodeById(id: NodeId): NodeData | null {
    return this.getNode(id);
  }
}
